import { motion } from 'framer-motion';
import { useUpdateCartItem, useRemoveCartItem } from '@/hooks/useCart';
import { primaryImageUrl } from '@/lib/cloudinary';
import { formatINR } from '@/lib/formatters';
import { effectivePricePaise } from '@/types/product';
import type { CartItem as CartItemType } from '@/types/cart';
import { cn } from '@/lib/cn';

interface CartItemProps {
  item: CartItemType;
  /** Compact = drawer view, Full = cart page row. */
  variant?: 'compact' | 'full';
  className?: string;
}

export function CartItem({ item, variant = 'compact', className }: CartItemProps) {
  const update = useUpdateCartItem();
  const remove = useRemoveCartItem();

  const imgUrl = primaryImageUrl(item.product.images, { width: 120, crop: 'fill' });
  const lineTotalPaise =
    effectivePricePaise(item.product, item.variant) * item.quantity;

  const decrement = () => {
    if (item.quantity <= 1) {
      remove.mutate(item.id);
      return;
    }
    update.mutate({ id: item.id, quantity: item.quantity - 1 });
  };

  const increment = () => {
    update.mutate({ id: item.id, quantity: item.quantity + 1 });
  };

  const removeItem = () => remove.mutate(item.id);

  const compact = variant === 'compact';

  return (
    <motion.li
      layout
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
      className={cn(
        'flex gap-4 border-b border-white/[0.06] py-4 last:border-b-0',
        className
      )}
    >
      <div
        className={cn(
          'relative shrink-0 overflow-hidden rounded-md bg-[color:var(--color-bg-surface)]',
          compact ? 'h-20 w-[60px]' : 'h-32 w-24'
        )}
      >
        {imgUrl && (
          <img
            src={imgUrl}
            alt={item.product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <h3
            className={cn(
              'font-[family-name:var(--font-editorial)] leading-snug text-white',
              compact ? 'text-sm line-clamp-2' : 'text-base'
            )}
          >
            {item.product.name}
          </h3>
          <button
            type="button"
            onClick={removeItem}
            aria-label="Remove item"
            className="-m-1 rounded p-1 text-[color:var(--color-text-muted)] transition-colors hover:bg-white/10 hover:text-white"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <p className="font-[family-name:var(--font-label)] text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-text-muted)]">
          size: {item.variant.size} · color: {item.variant.color}
        </p>

        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="inline-flex items-center rounded-md border border-white/15 bg-white/[0.03]">
            <button
              type="button"
              onClick={decrement}
              aria-label="Decrease quantity"
              className="inline-flex h-8 w-8 items-center justify-center text-[color:var(--color-text-secondary)] transition-colors hover:bg-white/5 hover:text-white"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <span
              aria-live="polite"
              className="inline-flex h-8 min-w-8 items-center justify-center px-2 font-[family-name:var(--font-label)] text-sm text-white"
            >
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={increment}
              aria-label="Increase quantity"
              className="inline-flex h-8 w-8 items-center justify-center text-[color:var(--color-text-secondary)] transition-colors hover:bg-white/5 hover:text-white"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
          <span className="font-[family-name:var(--font-display)] text-base text-white">
            {formatINR(lineTotalPaise)}
          </span>
        </div>
      </div>
    </motion.li>
  );
}
